import React from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import EmptyImage from "../../components/EmptyImage.tsx";
import {
  findAllFaqs,
  deleteFaq,
  Faq,
} from "../../services/faqs.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../../components/Loader.tsx";
import FaqForm from "../../components/faqs/FaqForm.tsx";

const FaqsPage = () => {
  const [loading, setLoading] = React.useState(false);
  const [faqs, setFaqs] = React.useState<Faq[]>([]);
  const [total, setTotal] = React.useState(0);
  const [modal, setModal] = React.useState(false);
  const [editModal, setEditModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedFaq, setSelectedFaq] = React.useState<Faq | null>(null);
  const [searchParam, setSearchParam] = React.useState("");
  const [limit] = React.useState(100);
  const [offset, setOffset] = React.useState(0);

  useAppSettings();

  const formatDate = (date: string) => {
    if (!date || date === "-" || date === "" || date === "Invalid Date")
      return "-";

    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "-";

      return dateObj.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await findAllFaqs({
        limit,
        offset,
        param: searchParam,
      });
      setFaqs(response.faqs || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error(error);
      notification("Error ⚠️", "Error al cargar las FAQs", "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchFaqs();
  }, [offset, searchParam]);

  const handleOpenModal = () => {
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
    setSelectedFaq(null);
  };

  const handleEdit = (faq: Faq) => {
    setSelectedFaq(faq);
    setEditModal(true);
  };

  const handleDelete = (faq: Faq) => {
    setSelectedFaq(faq);
    setDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setSelectedFaq(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSelectedFaq(null);
  };

  const handleDeleteSuccess = async () => {
    if (!selectedFaq) return;

    try {
      setLoading(true);
      await deleteFaq(selectedFaq.id);
      notification("", "FAQ eliminada exitosamente", "success");
      fetchFaqs();
      handleCloseDeleteModal();
    } catch (error) {
      notification("Error ⚠️", "Error al eliminar la FAQ", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    fetchFaqs();
    handleCloseModal();
  };

  const handleEditSuccess = () => {
    fetchFaqs();
    handleCloseEditModal();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(e.target.value);
    setOffset(0);
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Configuración"
        basePath="/configuracion"
        activeTitle="Preguntas Frecuentes"
        title={"Preguntas Frecuentes (FAQs)"}
      />

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Gestión de FAQs</span>
              <button
                className="btn btn-warning btn-sm"
                onClick={handleOpenModal}
              >
                <i className="fa fa-plus me-2"></i>
                Nueva FAQ
              </button>
            </div>
          </PanelHeader>
          <PanelBody>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-muted small">
                  {`${total} FAQ${total !== 1 ? "s" : ""} encontrada${total !== 1 ? "s" : ""}`}
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar por pregunta o respuesta..."
                    value={searchParam}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div
                className="table-responsive mb-3"
                style={{
                  overflowY: "auto",
                  maxHeight: "600px",
                  minHeight: "400px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                }}
              >
                <table className="table table-striped table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "30%",
                        }}
                      >
                        Pregunta
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "40%",
                        }}
                      >
                        Respuesta
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "15%",
                        }}
                      >
                        Multimedia
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "15%",
                        }}
                      >
                        Fecha creación
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "15%",
                        }}
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {faqs.map((faq, idx) => (
                      <tr
                        key={faq.id || idx}
                        style={{ borderBottom: "1px solid #f1f3f4" }}
                      >
                        <td
                          className="py-3 px-3 fw-medium"
                          style={{ width: "30%" }}
                        >
                          {truncateText(faq.question, 80)}
                        </td>
                        <td
                          className="py-3 px-3"
                          style={{
                            width: "40%",
                            wordWrap: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={faq.answer}
                        >
                          {truncateText(faq.answer, 120)}
                        </td>
                        <td
                          className="py-3 px-3"
                          style={{ width: "15%" }}
                        >
                          {faq.imageUrl || faq.mediaLink ? (
                            <span className="badge bg-success">
                              <i className="fa fa-check me-1"></i>
                              Sí
                            </span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        <td
                          className="py-3 px-3"
                          style={{ width: "15%" }}
                        >
                          {formatDate(faq.createdAt)}
                        </td>
                        <td
                          className="py-3 px-3"
                          style={{ width: "15%" }}
                        >
                          <button
                            className="btn btn-sm btn-info me-1"
                            onClick={() => handleEdit(faq)}
                            title="Editar FAQ"
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(faq)}
                            title="Eliminar FAQ"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {faqs.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center text-muted py-5"
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <div className="d-flex flex-column align-items-center justify-content-center">
                              <EmptyImage className="mx-auto w-250px" />
                              <h5 className="text-secondary text-center fs-20px mt-3">
                                No hay FAQs guardadas
                              </h5>
                              <p className="text-muted">
                                Crea tu primera FAQ para ayudar a los usuarios
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Modal para crear nueva FAQ */}
      <ModalContainer
        open={modal}
        closeFn={handleCloseModal}
        title="Nueva FAQ"
        size="lg"
      >
        <FaqForm
          close={handleCloseModal}
          onSuccess={handleCreateSuccess}
        />
      </ModalContainer>

      {/* Modal para editar FAQ */}
      <ModalContainer
        open={editModal}
        closeFn={handleCloseEditModal}
        title="Editar FAQ"
        size="lg"
      >
        <FaqForm
          close={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          faqToEdit={selectedFaq}
          isEditing={true}
        />
      </ModalContainer>

      {/* Modal para confirmar eliminación */}
      <ModalContainer
        open={deleteModal}
        closeFn={handleCloseDeleteModal}
        title="Eliminar FAQ"
        size="sm"
      >
        <div className="p-4">
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>¿Estás seguro?</strong>
          </div>
          <p className="mb-3">
            Se eliminará permanentemente la FAQ{" "}
            <strong>"{selectedFaq?.question}"</strong>. Esta acción no se puede deshacer.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={handleCloseDeleteModal}
            >
              Cancelar
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteSuccess}
            >
              <i className="fa fa-trash me-1"></i>
              Eliminar
            </button>
          </div>
        </div>
      </ModalContainer>
    </Layout>
  );
};

export default FaqsPage;

